import React, { Component } from "react";
import styled from "styled-components";
import { TextField, Button } from "@material-ui/core";
import moment from "moment";

import Loadable from "js/react/components/Loadable";
import { Section, UIPane, UIPaneContents, UIPaneHeader } from "js/react/components/UiComponents";
import { FlexBox } from "js/react/components/LayoutGrid";
import { FlexSpacer, Gap20, Gap30 } from "js/react/components/Gap";
import { SlideThumbnail } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import Avatar from "js/react/components/Avatar";
import getUserProfile from "js/core/services/userProfiles";
import { clipboardWrite, ClipboardType } from "js/core/utilities/clipboard";
import { ShowSnackBar } from "js/react/components/Dialogs/BaseDialog";
import { withTranslation } from "react-i18next";

const ThumbnailContainer = styled.div`
    width: 380px;
    height: 214px;
    position: relative;
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    label {
        font-size: 13px;
        width: 80px;
    }
    .avatar {
        margin-right: 10px;
    }
    .name {
        font-size: 14px;
        font-weight: 600;
        color: #333;
    }
    .date { 
        margin-left: 20px;
        font-size: 14px;
        color: #666;
    }
`;

const LastViewedInfo = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    label {
        font-size: 13px;
        width: 80px;
    }
    .date {
        margin-left: 20px;
        font-size: 14px;
        color: #666;
    }
`;

const GeneratedWithAiInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: 10px;
    label {
        font-size: 12px;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
    }
    .copy-prompt {
        width: 220px;
        align-self: flex-end;
    }
`;

class PropertiesPane extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            createdBy: null,
            modifiedBy: null,
            name: props.presentation.get("name"),
        };
    }

    async componentDidMount() {
        const { presentation } = this.props;

        const createdAt = presentation.get("createdAt");
        const createdBy = await getUserProfile(presentation.get("userId"));

        const lastModifiedData = await presentation.getLastModifiedData();
        const modifiedAt = lastModifiedData.lastModifiedAt;
        const modifiedBy = lastModifiedData.lastModifiedBy ? await getUserProfile(lastModifiedData.lastModifiedBy) : null;

        this.setState({ isLoading: false, createdAt, createdBy, modifiedAt, modifiedBy });
    }

    handleSaveName = async () => {
        const { presentation } = this.props;
        const { name } = this.state;

        if (presentation.get("name") !== name) {
            presentation.update({ name });
            await presentation.updatePromise;
        }
    }

    handleCopyPrompt = (isImageStylePrompt = false) => {
        const { prompt, imageStylePrompt } = this.getAiGeneratedInfo();

        clipboardWrite({ [ClipboardType.TEXT]: isImageStylePrompt ? imageStylePrompt : prompt }, true);
        const { t } = this.props;
        ShowSnackBar({ message: t("{{type}} was copied to the clipboard", { type: isImageStylePrompt ? t("Image style prompt") : t("Prompt") }) });
    }

    renderName(user) {
        if (user.displayName) {
            return <span className="fullName">{user.displayName}</span>;
        } else {
            return <span className="fullName">{user.email}</span>;
        }
    }

    getAiGeneratedInfo = () => {
        const { presentation } = this.props;

        const metadata = presentation.get("metadata");
        if (!metadata || (!metadata.prompt && !metadata.finalPrompt)) {
            return null;
        }

        const parseOriginalPrompt = finalPrompt => {
            if (!finalPrompt) {
                return null;
            }

            const prefixRemoved = finalPrompt.split("Create a presentation for this prompt: \"")[1];
            if (!prefixRemoved) {
                return null;
            }

            const suffixRemoved = prefixRemoved.substring(0, prefixRemoved.indexOf("\""));
            return suffixRemoved;
        };

        const prompt = metadata.userPrompt || parseOriginalPrompt(metadata.finalPrompt);
        const imageStylePrompt = metadata.imageStylePrompt;

        return { prompt, imageStylePrompt };
    }

    render() {
        const { presentation, handleViewAnalytics, t } = this.props;
        const { isLoading, createdAt, createdBy, modifiedAt, modifiedBy, name } = this.state;

        const { prompt, imageStylePrompt } = this.getAiGeneratedInfo() || {};

        return (
            <UIPane>
                <UIPaneHeader>{t("Presentation Properties")}</UIPaneHeader>
                <UIPaneContents>
                    <Loadable isLoading={isLoading}>
                        <Section>
                            <FlexBox top left padding="10px 0">
                                <ThumbnailContainer>
                                    <SlideThumbnail
                                        slideId={presentation.getSips()[0]}
                                        highQuality
                                        style={{ width: "380px", height: "214px" }}
                                    />
                                </ThumbnailContainer>
                                <Gap30 />
                                <FlexBox column top left fill minHeight="214px">
                                    <TextField
                                        id="presentation-name"
                                        label={t("Name")}
                                        fullWidth
                                        autoFocus
                                        value={name}
                                        onChange={event => this.setState({ name: event.target.value })}
                                        onBlur={() => this.handleSaveName()}
                                    />

                                    <Gap30 />
                                    {createdBy && <>
                                        <UserInfo>
                                            <label>{t("Created by")}</label>
                                            <Avatar url={createdBy.photoURL} />
                                            <div className="name">{this.renderName(createdBy)}</div>
                                            <div className="date">{moment.utc(createdAt).local().format("LL")}</div>
                                        </UserInfo>
                                        <Gap20 />
                                    </>}
                                    {modifiedBy && <>
                                        <UserInfo>
                                            <label>{t("Modified by")}</label>
                                            <Avatar url={modifiedBy.photoURL} />
                                            <div className="name">{this.renderName(modifiedBy)}</div>
                                            <div className="date">{moment.utc(modifiedAt).local().format("LL")}</div>
                                        </UserInfo>
                                        <Gap20 />
                                    </>}

                                    <FlexSpacer />
                                    {presentation.get("lastViewedAt") && (
                                        <FlexBox horizontalAlign="between" center fillWidth>
                                            <LastViewedInfo>
                                                <label>{t("Last viewed")}</label>
                                                <div className="date">{moment.utc(presentation.get("lastViewedAt")).local().format("LL")}</div>
                                            </LastViewedInfo>
                                            <Button variant="text" color="primary" style={{ width: "150px" }} onClick={() => handleViewAnalytics()}>
                                                {t("View Analytics")}
                                            </Button>
                                        </FlexBox>
                                    )}
                                </FlexBox>
                            </FlexBox>
                        </Section>

                        {(prompt || imageStylePrompt) && <>
                            <Gap20 />
                            <Section title={t("This presentation was generated with AI")}>
                                {prompt && <>
                                    <FlexBox column top left fill>
                                        <GeneratedWithAiInfo>
                                            <label>{t("Prompt Used")}</label>
                                            <TextField
                                                id="presentation-prompt"
                                                variant="outlined"
                                                fullWidth
                                                value={prompt}
                                                disabled
                                            />
                                            <Button variant="text" color="primary" className="copy-prompt" onClick={() => this.handleCopyPrompt()}>
                                                {t("Copy Prompt to Clipboard")}
                                            </Button>
                                        </GeneratedWithAiInfo>
                                    </FlexBox>
                                </>}
                                {imageStylePrompt && <>
                                    <FlexBox column top left fill>
                                        <GeneratedWithAiInfo>
                                            <label>{t("Image Style Prompt Used")}</label>
                                            <TextField
                                                id="presentation-image-style-prompt"
                                                variant="outlined"
                                                fullWidth
                                                value={imageStylePrompt}
                                                disabled
                                            />
                                            <Button variant="text" color="primary" className="copy-prompt" onClick={() => this.handleCopyPrompt(true)}>
                                                {t("Copy Prompt to Clipboard")}
                                            </Button>
                                        </GeneratedWithAiInfo>
                                    </FlexBox>
                                </>}
                            </Section>
                        </>}
                    </Loadable>
                </UIPaneContents>
            </UIPane>
        );
    }
}

export default withTranslation()(PropertiesPane);
